"use strict";
/**
 * `@sooth-labs/idl` — the TypeScript peer of `prod/sooth-idl`.
 *
 * The Sooth forecast type system (`OutcomeSpace`, `Metric`, `Pmf`,
 * `ForecastValue`) and the forecast **progress vocabulary**, both
 * generated from the same TypeSpec-authored JSON Schema as the pydantic models —
 * so the browser and the server cannot drift.
 *
 * `src/schema.ts` is generated; this module is the hand-written surface over it
 * and declares no field of its own. Changing what a type carries means editing
 * the `.tsp` in the Python package and regenerating both sides.
 *
 * The handful of functions here are ports, not new logic: each mirrors a
 * `sooth_idl` function over the same fields with the same tolerance, because a
 * browser cannot call Python. Where the two could drift, they are pinned by
 * tests on both sides.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EMPTY_FORECAST = exports.FIRST_ARRIVAL_ORDER = exports.BOOLEAN_LABELS = exports.OTHER_LABEL = void 0;
exports.onDay = onDay;
exports.uniformBetween = uniformBetween;
exports.validateTiming = validateTiming;
exports.expectedWindow = expectedWindow;
exports.supportSize = supportSize;
exports.validateBinning = validateBinning;
exports.effectiveEdgeOwners = effectiveEdgeOwners;
exports.binLabels = binLabels;
exports.outcomeLabels = outcomeLabels;
exports.validatePmf = validatePmf;
exports.familyOf = familyOf;
exports.isContractKey = isContractKey;
exports.isOutcomeSpace = isOutcomeSpace;
exports.isContractDraft = isContractDraft;
exports.parseUpdate = parseUpdate;
exports.checkUpdate = checkUpdate;
exports.isUnknownUpdate = isUnknownUpdate;
exports.massOf = massOf;
exports.validateContinuousForecast = validateContinuousForecast;
exports.validateForecast = validateForecast;
exports.validateDecomposition = validateDecomposition;
exports.impact = impact;
exports.shiftToward = shiftToward;
exports.isFinished = isFinished;
exports.isFinal = isFinal;
exports.openToolCalls = openToolCalls;
exports.applyUpdate = applyUpdate;
exports.updatesSince = updatesSince;
exports.replay = replay;
/** Timing for a trigger expected on one day. */
function onDay(day) {
    return [{ starts: day, ends: day, mass: 1 }];
}
/**
 * Timing spread evenly across a window — the old `expected_from`/`expected_to`.
 *
 * Kept as a one-call constructor precisely because it is the common case. What
 * changed is that it is now a *choice*: a producer that knows the trigger is
 * likelier at the March meeting than in April can say so, instead of flattening
 * it into a window that reads as if it did not know.
 */
function uniformBetween(starts, ends) {
    if (ends < starts) {
        throw new Error(`window ends ${ends} before it starts ${starts}`);
    }
    return [{ starts, ends, mass: 1 }];
}
/**
 * Throw unless `when` is a coherent distribution over time.
 *
 * Ordered, non-overlapping, each slice well-formed, and summing to 1 — none of
 * which JSON Schema can state. Overlap is the one worth naming: two slices
 * covering the same day double-count that day's probability, and nothing
 * downstream would notice. Dates are ISO, so string comparison is date order.
 */
function validateTiming(when, tol = TOLERANCE) {
    if (when.length === 0) {
        throw new Error("timing needs at least one slice");
    }
    let previous = null;
    for (const slice of when) {
        if (slice.ends < slice.starts) {
            throw new Error(`slice ends ${slice.ends} before it starts ${slice.starts}`);
        }
        if (previous !== null && slice.starts <= previous.ends) {
            throw new Error(`slices must be ordered and non-overlapping: ` +
                `${slice.starts} follows a slice ending ${previous.ends}`);
        }
        previous = slice;
    }
    const total = when.reduce((acc, slice) => acc + slice.mass, 0);
    if (Math.abs(total - 1) > tol) {
        throw new Error(`timing sums to ${total}, not 1`);
    }
}
/**
 * The span a timing distribution covers, for a consumer that only draws a bar.
 *
 * Derived, never carried: a renderer that cannot show a distribution should not
 * make the producer send it a second, coarser field.
 */
function expectedWindow(when) {
    return {
        starts: when.reduce((a, s) => (s.starts < a ? s.starts : a), when[0].starts),
        ends: when.reduce((a, s) => (s.ends > a ? s.ends : a), when[0].ends),
    };
}
/** Default tolerance on a mass vector's sum and on the total-probability identity. Matches `sooth_idl`. */
const TOLERANCE = 1e-9;
/**
 * The number of outcomes in `space`'s finite support — the required PMF length.
 *
 * Port of `sooth_idl.support_size`. `allow_other` on a categorical space adds one
 * implicit OTHER slot; open numeric tail bins each add one.
 */
function supportSize(space) {
    switch (space.kind) {
        case "continuous":
            throw new Error("unbinned numerical spaces have no finite support or bin labels");
        case "boolean":
            return 2;
        case "categorical":
            return space.labels.length + (space.allow_other ? 1 : 0);
        case "structured":
            return space.labels.length;
        case "numeric": {
            const { edges, open_low, open_high } = space.binning;
            return edges.length - 1 + (open_low ? 1 : 0) + (open_high ? 1 : 0);
        }
    }
}
/** The label of a categorical space's implicit OTHER slot — lowercase, and last. */
exports.OTHER_LABEL = "other";
/** A boolean space's two slots, in PMF index order (0 = no, 1 = yes). */
exports.BOOLEAN_LABELS = ["no", "yes"];
/** Significant digits in a bin-edge label — `%g`'s default precision. */
const EDGE_PRECISION = 6;
/**
 * Magnitude cap on a declared tail representative.
 *
 * Port of `sooth_idl.shapes._MAX_TAIL_REP`. Past this the tail term dominates
 * the expected score in float64, so interior discrimination is lost and scores
 * stop being comparable across questions.
 */
const MAX_TAIL_REP = 1e12;
/**
 * Throw unless `binning` is usable as a numeric support.
 *
 * Port of `sooth_idl.validate_binning`, which the Python labeller runs on every
 * call and this one did not — so a binning Python rejects outright produced
 * labels here. None of it is expressible in JSON Schema (which can say
 * "ascending" in prose and enforce only `minItems(2)`), and none of it fails
 * loudly downstream: reversed edges silently invert every interval label and
 * make the scorer's coordinates non-monotone, while a tail representative on or
 * inside the outer edge puts two distinct outcomes at the same coordinate.
 *
 * Tail representatives are checked whenever present, whether or not that tail is
 * currently open: storing an unusable one is an authoring bug that would change
 * every distance the moment the tail is opened, so it surfaces when written.
 */
function validateBinning(binning) {
    effectiveEdgeOwners(binning);
    const edges = binning.edges;
    if (!edges.every((edge) => Number.isFinite(edge))) {
        throw new Error(`binning edges must all be finite, got ${JSON.stringify(edges)}`);
    }
    if (edges.some((edge, index) => index > 0 && edges[index - 1] >= edge)) {
        throw new Error(`binning edges must be strictly increasing, got ${JSON.stringify(edges)}`);
    }
    if (binning.rep_low != null) {
        checkTailRep("rep_low", binning.rep_low, edges[0], true);
    }
    if (binning.rep_high != null) {
        checkTailRep("rep_high", binning.rep_high, edges[edges.length - 1], false);
    }
}
/** Equality owner at each edge, including the absent-field legacy rule. */
function effectiveEdgeOwners(binning) {
    if (binning.edge_owners != null) {
        if (binning.edge_owners.length !== binning.edges.length ||
            binning.edge_owners.some(owner => owner !== "lower" && owner !== "upper")) {
            throw new Error("edge_owners must contain one lower or upper owner per bin edge");
        }
        return [...binning.edge_owners];
    }
    return binning.edges.map((_, index) => index === binning.edges.length - 1 && !binning.open_high ? "lower" : "upper");
}
/** Validate one declared tail representative against the edge it must lie beyond. */
function checkTailRep(name, rep, edge, below) {
    if (!Number.isFinite(rep))
        throw new Error(`${name} must be finite, got ${rep}`);
    if (below ? rep >= edge : rep <= edge) {
        throw new Error(`${name}=${rep} must be strictly ${below ? "below" : "above"} the outer edge ${edge}; ` +
            "a representative on or inside the edge collides with an interior bin's coordinate");
    }
    if (Math.abs(rep) > MAX_TAIL_REP) {
        throw new Error(`|${name}|=${Math.abs(rep)} exceeds the ${fmtEdge(MAX_TAIL_REP)} magnitude cap`);
    }
}
/**
 * Compact numeric label — Python's `%g`, reimplemented.
 *
 * A bin label is part of the wire contract's meaning: both languages label the
 * same space for the same reader, so `>= 1.23457e+06` in one and `>= 1234570`
 * in the other is a real divergence, not cosmetics. JavaScript has no `%g` and
 * its defaults are nowhere near it — `String(Number(...))` reaches for
 * exponential only past 1e21 (`%g` does so at 1e6), holds it off until 1e-7
 * (`%g`: 1e-5), and never pads the exponent to the two digits `%g` always
 * writes. So this is spelled out rather than approximated.
 *
 * The rule: with `P` significant digits and `X` the decimal exponent *after*
 * rounding to `P`, use positional notation when `-4 <= X < P` and exponential
 * otherwise; either way drop trailing fractional zeros, and a bare trailing
 * point with them.
 */
function fmtEdge(value) {
    if (Number.isNaN(value))
        return "nan";
    if (!Number.isFinite(value))
        return value > 0 ? "inf" : "-inf";
    if (value === 0)
        return "0";
    const [mantissa = "", exponent = "0"] = value.toExponential(EDGE_PRECISION - 1).split("e");
    const scale = Number(exponent);
    if (scale >= -4 && scale < EDGE_PRECISION) {
        return trimZeros(value.toFixed(EDGE_PRECISION - 1 - scale));
    }
    const sign = scale < 0 ? "-" : "+";
    return `${trimZeros(mantissa)}e${sign}${String(Math.abs(scale)).padStart(2, "0")}`;
}
/** Drop trailing fractional zeros, and the point if nothing survives it. */
function trimZeros(rendered) {
    // Guarded on the point: without it this eats the zeros of an integer like
    // `100`, which `%g` keeps.
    return rendered.includes(".") ? rendered.replace(/\.?0+$/, "") : rendered;
}
// Port of the integer-grid branch of `sooth_idl.bin_labels`: a count cut at k + 0.5 is
// spelled by the whole values each bin holds ("7", "7–8", "<= 6", ">= 10"). Same tolerance,
// same EN DASH, same clipped-outer-edge rule; the goldens hold the two in step.
const GRID_EPS = 1e-9;
const RANGE_DASH = "\u2013";
function isHalfInteger(value) {
    return Math.abs(value - Math.floor(value) - 0.5) < GRID_EPS;
}
function isInteger(value) {
    return Math.abs(value - Math.round(value)) < GRID_EPS;
}
/**
 * Every edge a half-integer, except that an OUTER edge may be the whole value it was
 * clipped to, and at least one bin a full step of the grid (both edges half-integer) —
 * which is what keeps `[0, 0.5, 1]`, a share rather than a count, in the interval form.
 */
function isIntegerGrid(edges) {
    if (!edges.slice(1, -1).every(isHalfInteger))
        return false;
    for (const outer of [edges[0], edges[edges.length - 1]]) {
        if (!(isHalfInteger(outer) || isInteger(outer)))
            return false;
    }
    return edges.some((left, i) => i < edges.length - 1 && isHalfInteger(left) && isHalfInteger(edges[i + 1]));
}
/** The whole values `[left, right)` (or `[left, right]`) holds, as `[lo, hi]`. */
function integersIn(left, right, closed) {
    const lo = Math.ceil(left - GRID_EPS);
    const hi = closed ? Math.floor(right + GRID_EPS) : Math.ceil(right - GRID_EPS) - 1;
    return [lo, hi];
}
function integerGridLabels(binning) {
    const edges = [...binning.edges];
    const labels = [];
    if (binning.open_low)
        labels.push(`<= ${fmtEdge(Math.ceil(edges[0] - GRID_EPS) - 1)}`);
    const last = edges.length - 2;
    for (let index = 0; index < edges.length - 1; index += 1) {
        const closed = index === last && !binning.open_high;
        const [lo, hi] = integersIn(edges[index], edges[index + 1], closed);
        labels.push(lo === hi ? fmtEdge(lo) : `${fmtEdge(lo)}${RANGE_DASH}${fmtEdge(hi)}`);
    }
    if (binning.open_high)
        labels.push(`>= ${fmtEdge(Math.ceil(edges[edges.length - 1] - GRID_EPS))}`);
    return labels;
}
/**
 * Human-readable label per numeric slot, in PMF index order (incl. open tails).
 *
 * Explicit equality ownership controls brackets and tails; absent ownership keeps
 * the legacy membership and integer-grid label spelling.
 */
function binLabels(binning) {
    validateBinning(binning);
    const edges = [...binning.edges];
    if (binning.edge_owners == null && isIntegerGrid(edges))
        return integerGridLabels(binning);
    const owners = effectiveEdgeOwners(binning);
    const labels = [];
    if (binning.open_low)
        labels.push(`${owners[0] === "lower" ? "<=" : "<"} ${fmtEdge(edges[0])}`);
    for (let index = 0; index < edges.length - 1; index += 1) {
        const opening = owners[index] === "upper" ? "[" : "(";
        const closing = owners[index + 1] === "lower" ? "]" : ")";
        labels.push(`${opening}${fmtEdge(edges[index])}, ${fmtEdge(edges[index + 1])}${closing}`);
    }
    if (binning.open_high)
        labels.push(`${owners[owners.length - 1] === "upper" ? ">=" : ">"} ${fmtEdge(edges[edges.length - 1])}`);
    return labels;
}
/**
 * Human-readable label per support slot, in PMF index order, for any space.
 *
 * Port of `sooth_idl.outcome_labels`, which is why the browser can name a
 * numeric question's buckets without the scorer: a label is derivable from the
 * space alone. `outcomeLabels(space)[i]` names the slot that `mass[i]` weighs.
 */
function outcomeLabels(space) {
    switch (space.kind) {
        case "continuous":
            throw new Error("unbinned numerical spaces have no finite support or bin labels");
        case "numeric":
            return binLabels(space.binning);
        case "categorical":
            return space.allow_other ? [...space.labels, exports.OTHER_LABEL] : [...space.labels];
        case "structured":
            return [...space.labels];
        case "boolean":
            return [...exports.BOOLEAN_LABELS];
    }
}
/**
 * Throw unless `mass` is a coherent PMF over `space`.
 *
 * Port of `sooth_idl.validate_pmf`: length equals the support size, every entry
 * is finite and non-negative, and the entries sum to 1 within `tol`. None of it
 * is expressible in JSON Schema, and an incoherent PMF does not fail loudly
 * downstream — it renders as a plausible wrong number.
 */
function validatePmf(mass, space, tol = TOLERANCE) {
    const expected = supportSize(space);
    if (mass.length !== expected) {
        throw new Error(`PMF length ${mass.length} !== support size ${expected}`);
    }
    if (mass.some((m) => !Number.isFinite(m) || m < 0)) {
        throw new Error("PMF has a non-finite or negative mass entry");
    }
    const total = mass.reduce((a, b) => a + b, 0);
    if (Math.abs(total - 1) > tol) {
        throw new Error(`PMF sums to ${total}, not 1 (tol=${tol})`);
    }
}
/**
 * The order a well-behaved producer FIRST emits each MILESTONE kind — for a
 * progress display, not for ordering. Ordering is `seq`: every kind here may
 * recur, so the sequence of kinds says nothing about which estimate is current.
 *
 * Deliberately NOT every update kind. This ranked all nine and `parseUpdate`
 * took its known-kind set from these keys, which welded the two together: the
 * ladder and the parser could not disagree with each other, only with Python,
 * where the same constant has only the six milestones. They are separate now —
 * `ALL_UPDATE_KINDS` is what the parser reads — and both are checked against
 * goldens Python generates.
 */
exports.FIRST_ARRIVAL_ORDER = Object.freeze({
    stream_opened: 1,
    contract_updated: 2,
    past_trigger_upserted: 3,
    future_trigger_upserted: 4,
    forecast_updated: 5,
    report_updated: 6,
});
/**
 * Every `kind` this build recognizes — what `parseUpdate` decides on.
 *
 * A `Record` keyed by the tag union rather than a plain set, so a kind added to
 * the contract fails to compile here until it is listed, instead of silently
 * parsing as unknown.
 */
const ALL_UPDATE_KINDS = Object.freeze({
    stream_opened: true,
    contract_updated: true,
    past_trigger_upserted: true,
    future_trigger_upserted: true,
    forecast_updated: true,
    continuous_forecast_updated: true,
    report_updated: true,
    trigger_removed: true,
    tool_call_started: true,
    tool_call_updated: true,
    tool_call_finished: true,
    part_finalized: true,
    run_finished: true,
});
/**
 * Which answer shape a space's forecasts take: one probability (`boolean`), mass
 * over a finite support (`distribution`; a binned numerical space may also carry
 * a continuous headline), or an unbinned numerical preview (`continuous`).
 */
function familyOf(space) {
    return space.kind === "continuous" ? "continuous" : space.kind === "boolean" ? "boolean" : "distribution";
}
/** Whitespace as the contract spells it (`WHITESPACE` in `contract.tsp`). */
const WHITESPACE = "\\u0009-\\u000D\\u001C-\\u0020\\u0085\\u00A0\\u1680\\u2000-\\u200A\\u2028\\u2029\\u202F\\u205F\\u3000\\uFEFF";
/** A contract's text is never blank: it holds a character that is not `WHITESPACE`. */
const NOT_BLANK = new RegExp(`[^${WHITESPACE}]`);
/** A `ContractKey`: `<Source>#<id>`, the id holding no whitespace, at most 512 long. */
const CONTRACT_KEY = new RegExp(`^[A-Za-z][A-Za-z0-9_]*#[^${WHITESPACE}]+$`);
function isText(value) {
    return typeof value === "string" && NOT_BLANK.test(value);
}
/** Whether `value` is a `ContractKey`. */
function isContractKey(value) {
    // At most 512 characters as JSON Schema and Python count them: code points, not UTF-16 units.
    return typeof value === "string" && [...value].length <= 512 && CONTRACT_KEY.test(value);
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** At least two labels, each a string: a finite space's support (`minItems(2)`). */
function isLabels(value) {
    return Array.isArray(value) && value.length >= 2 && value.every((label) => typeof label === "string");
}
/**
 * Whether `value` is an `OutcomeSpace` this build can read: a known `kind` with the fields that
 * variant requires (labels, a metric, bounds and a usable binning, a domain). Extra fields are
 * tolerated, as everywhere in the stream. Python validates the same through its models.
 */
function isOutcomeSpace(value) {
    if (!isRecord(value))
        return false;
    switch (value["kind"]) {
        case "boolean":
            return true;
        case "categorical":
            return (isLabels(value["labels"]) &&
                (value["allow_other"] === undefined || typeof value["allow_other"] === "boolean") &&
                (value["metric"] === undefined || value["metric"] === null || isMetric(value["metric"])));
        case "structured":
            return isLabels(value["labels"]) && isMetric(value["metric"]);
        case "numeric": {
            if (typeof value["lo"] !== "number" || typeof value["hi"] !== "number" || !isRecord(value["binning"]))
                return false;
            const binning = value["binning"];
            // At least two numeric edges (`minItems(2)`), which validateBinning leaves to the schema.
            const edges = binning["edges"];
            if (!Array.isArray(edges) || edges.length < 2 || !edges.every((edge) => typeof edge === "number"))
                return false;
            // Stated tail flags are booleans and stated tail representatives numbers, as the
            // schema types them: `supportSize` counts a truthy flag as a bin.
            if (!["open_low", "open_high"].every((flag) => binning[flag] === undefined || typeof binning[flag] === "boolean")) {
                return false;
            }
            if (!["rep_low", "rep_high"].every((rep) => binning[rep] == null || typeof binning[rep] === "number"))
                return false;
            try {
                validateBinning(value["binning"]);
                return true;
            }
            catch {
                return false;
            }
        }
        case "continuous":
            return isRecord(value["domain"]);
        default:
            return false;
    }
}
/**
 * Whether `value` is a `ContractDraft`: its question and space stated (a draft replaces the
 * last whole, so it always restates both), and every other text it states not blank.
 *
 * The one body this package checks at runtime besides a continuous headline's, because the
 * fold reads the space off it: a draft with no space would leave the stream without one.
 */
function isContractDraft(value) {
    if (!isRecord(value))
        return false;
    const draft = value;
    if (!isText(draft["question"]) || !isOutcomeSpace(draft["outcome_space"]))
        return false;
    if (draft["resolution_date"] !== undefined && typeof draft["resolution_date"] !== "string")
        return false;
    if (draft["resolves_at"] !== undefined && typeof draft["resolves_at"] !== "string")
        return false;
    // At most one close, as the schema and the Python type require.
    if (draft["resolves_at"] !== undefined && draft["resolution_date"] !== undefined)
        return false;
    return ["resolution_criteria", "background", "resolution_summary"].every((field) => draft[field] === undefined || isText(draft[field]));
}
/**
 * Whether a payload carries a usable envelope.
 *
 * There is no runtime schema here, so a known `kind` alone is not enough to
 * trust a payload: `applyUpdate` reads `trace_id` and `seq` off every update,
 * and a malformed one would fold `undefined` into the document rather than
 * degrading. Checking the envelope is what keeps this peer's tolerance contract
 * the same as the Python one, where a malformed known-kind payload becomes an
 * `UnknownUpdate`.
 *
 * Most bodies are not checked — that would need a schema validator this package
 * deliberately does not carry. So the asymmetry with Python is real and bounded:
 * both degrade on a broken envelope, and on the bodies the fold relies on
 * (`hasReadableBody`); only Python validates the rest.
 */
function hasEnvelope(payload) {
    const seq = payload["seq"];
    return (typeof payload["trace_id"] === "string" &&
        typeof seq === "number" &&
        isUsableSeq(seq) &&
        typeof payload["occurred_at"] === "string");
}
/**
 * Whether `seq` is a cursor both languages accept.
 *
 * The contract bounds it to `[0, Number.MAX_SAFE_INTEGER]`; this checked only
 * `Number.isInteger`, so the two disagreed at both ends. Python rejected an
 * out-of-range `seq` and demoted the update to an unknown one — freezing its
 * document — while a TypeScript consumer folding the very same stream carried
 * on updating. Whatever the bound is, both sides have to enforce it, or one
 * silently stops and the other does not.
 */
function isUsableSeq(seq) {
    return Number.isSafeInteger(seq) && seq >= 0;
}
/**
 * Whether a recognized payload's body is one this build can fold.
 *
 * There is no runtime schema here, so this checks only what the fold relies on: a
 * continuous headline's preview, a draft's question and space, and a trigger contract that
 * is a draft or a key. With `space`, the space in force, every forecast and positional value
 * is checked against it too (`checkUpdate`).
 */
function hasReadableBody(payload, space) {
    try {
        if (payload["kind"] === "continuous_forecast_updated") {
            validateContinuousForecast(payload["forecast"]);
        }
        if (payload["kind"] === "stream_opened") {
            // The header states the stream's first space, which every later value is read against.
            if (typeof payload["question"] !== "string" || !isOutcomeSpace(payload["space"]))
                return false;
        }
        if (payload["kind"] === "contract_updated" && !isContractDraft(payload["contract"]))
            return false;
        if (payload["kind"] === "past_trigger_upserted") {
            // The fold upserts a past trigger by id and reads `moved_toward` as a slot.
            const past = payload["trigger"];
            if (!isRecord(past) || typeof past["id"] !== "string" || !isSupportIndex(past["moved_toward"]))
                return false;
        }
        if (payload["kind"] === "forecast_updated" && !isDiscreteForecast(payload["forecast"]))
            return false;
        const trigger = payload["kind"] === "future_trigger_upserted" ? payload["trigger"] : undefined;
        if (typeof trigger === "object" && trigger !== null) {
            const contract = trigger["contract"];
            if (contract !== undefined && !isContractDraft(contract) && !isContractKey(contract))
                return false;
            // A score's branches are each a DiscreteForecast; `null` spells an outlined trigger.
            const score = trigger["score"];
            if (score !== undefined && score !== null) {
                if (!isRecord(score) || !isDiscreteForecast(score["if_occurs"]) || !isDiscreteForecast(score["if_not"])) {
                    return false;
                }
            }
        }
        if (space)
            checkUpdate(space, payload);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Parse one wire payload, tolerating what this build does not recognize.
 *
 * An unrecognized `kind` yields an `UnknownUpdate` carrying the payload whole
 * rather than throwing. That is the whole forward-compatibility story: a
 * producer may ship a new update kind without a coordinated consumer deploy, and
 * the consumer degrades to "render it generically" instead of dropping the
 * stream. A recognized kind whose ENVELOPE or a body the fold relies on is
 * malformed degrades the same way, so a bad payload cannot fold `undefined` into
 * the document. Unknown *fields* on a recognized update ride along untouched,
 * since nothing here strips them.
 *
 * Pass `space`, the space in force (`state.space`), to also check the update's
 * forecast and positional values against it (`checkUpdate`); one that does not fit
 * degrades too. A `contract_updated` is not checked against it, since it states the
 * next one.
 */
function parseUpdate(payload, space) {
    const kind = payload["kind"];
    if (typeof kind === "string" && KNOWN_UPDATE_KINDS.has(kind) && hasEnvelope(payload) && hasReadableBody(payload, space)) {
        return payload;
    }
    const seq = payload["seq"];
    return {
        kind: typeof kind === "string" ? kind : "",
        // The same range check the envelope uses. An out-of-range cursor kept here
        // would be folded into `state.seq` and resumed from, at a magnitude where a
        // JavaScript number has already lost the precision to be a cursor at all.
        seq: typeof seq === "number" && isUsableSeq(seq) ? seq : null,
        data: payload,
    };
}
/**
 * Throw unless `update`'s forecast and positional values fit `space`, the space in force
 * when it was emitted. Port of `sooth_idl.progress.check_update`: a headline
 * (`validateForecast`), a future trigger's score (both branches, in one shape), and a past
 * trigger's `moved_toward` against the support. Every other update passes.
 */
function checkUpdate(space, update) {
    switch (update.kind) {
        case "forecast_updated":
            if (!isDiscreteForecast(update.forecast)) {
                throw new Error("forecast_updated carries a DiscreteForecast (bernoulli or pmf)");
            }
            validateForecast(space, update.forecast);
            return;
        case "continuous_forecast_updated":
            validateForecast(space, update.forecast);
            return;
        case "future_trigger_upserted": {
            const score = update.trigger.score;
            if (!score)
                return;
            if (!isDiscreteForecast(score.if_occurs) || !isDiscreteForecast(score.if_not)) {
                throw new Error("a trigger's branches are each a DiscreteForecast (bernoulli or pmf)");
            }
            if (score.if_occurs.form !== score.if_not.form) {
                throw new Error("a trigger's branches must take the same shape");
            }
            validateForecast(space, score.if_occurs);
            validateForecast(space, score.if_not);
            return;
        }
        case "past_trigger_upserted": {
            const slot = update.trigger.moved_toward;
            if (!isSupportIndex(slot)) {
                throw new Error(`moved_toward must be a support index (a non-negative integer), got ${JSON.stringify(slot)}`);
            }
            if (space.kind !== "continuous" && slot >= supportSize(space)) {
                throw new Error(`moved_toward ${slot} is outside the space's support`);
            }
            return;
        }
        default:
            return;
    }
}
/**
 * Whether `value` is a `DiscreteForecast` by form: a `bernoulli` or a `pmf`, the only forms a
 * headline (`forecast_updated`) or a trigger's branch takes. A continuous preview has no mass
 * vector and rides `continuous_forecast_updated` alone.
 */
function isDiscreteForecast(value) {
    return isRecord(value) && (value["form"] === "bernoulli" || value["form"] === "pmf");
}
/** A position in a support: an integer in `[0, 2^31 - 1]`, as the contract's `int32` with `@minValue(0)`. */
function isSupportIndex(value) {
    return Number.isInteger(value) && value >= 0 && value <= 2147483647;
}
/**
 * Whether `update` is the passthrough for a kind this build does not know.
 *
 * DERIVED, deliberately, from the same two conditions `parseUpdate` decides on
 * — never from a marker field. An `UnknownUpdate` used to carry `unknown: true`,
 * but unknown fields on a recognized update ride along untouched, so a producer
 * sending `{"kind": "forecast_updated", "unknown": true, …}` got a real forecast
 * update that every consumer here then treated as unrenderable. Anything a
 * producer can spell, a producer can forge; these two conditions it cannot,
 * because they are exactly what makes a payload recognized in the first place.
 *
 * Python gets this for free — its `UnknownUpdate` is a class and `isinstance` is
 * not something JSON can claim. This is that guarantee, in a language whose
 * parsed updates are plain objects (and stay plain, so a consumer can still
 * persist and rehydrate a log without losing the distinction).
 */
function isUnknownUpdate(update) {
    const payload = update;
    const kind = payload["kind"];
    return !(typeof kind === "string" && KNOWN_UPDATE_KINDS.has(kind) && hasEnvelope(payload) && hasReadableBody(payload));
}
const KNOWN_UPDATE_KINDS = new Set(Object.keys(ALL_UPDATE_KINDS));
/**
 * A forecast as a mass vector, whatever shape it was carried in.
 *
 * The one place a binary probability becomes `[1 - p, p]`. Everything treating
 * all three variants alike goes through here rather than re-deriving the binary
 * case, which is what keeps the scalar an encoding rather than a special case.
 */
function massOf(forecast) {
    if (forecast.form === "continuous")
        throw new Error("a continuous preview has no probability mass vector");
    return forecast.form === "bernoulli"
        ? [1 - forecast.probability, forecast.probability]
        : [...forecast.mass];
}
/**
 * Throw unless `forecast` is coherent for `space`.
 *
 * Checks what JSON Schema cannot: that the forecast's variant is the stream's
 * variant, that its length matches the declared space, and that its mass is
 * non-negative and sums to 1. Port of `sooth_idl.progress.validate_forecast`.
 */
function validateContinuousForecast(forecast) {
    if (!forecast || forecast.form !== "continuous" || !["cdf", "cdf_python"].includes(forecast.representation_form)
        || typeof forecast.artifact_ref !== "string" || !forecast.artifact_ref.length) {
        throw new Error("invalid continuous forecast envelope");
    }
    const p = forecast.preview;
    if (!p || ![p.p10, p.p50, p.p90].every(Number.isFinite) || !(p.p10 <= p.p50 && p.p50 <= p.p90)) {
        throw new Error("percentiles must be finite and ordered");
    }
    if (!Array.isArray(p.curve) || p.curve.length < 1 || p.curve.length > 4096
        || !Array.isArray(p.diagnostics) || !p.diagnostics.every(d => typeof d === "string")) {
        throw new Error("invalid continuous preview");
    }
    let previousX = -Infinity, previousRight = 0;
    for (const sample of p.curve) {
        if (!Array.isArray(sample) || sample.length !== 3 || !sample.every(Number.isFinite))
            throw new Error("invalid preview sample");
        const [x, left, right] = sample;
        if (!(x > previousX && previousRight <= left && left <= right && right <= 1))
            throw new Error("preview must be ordered and monotone");
        previousX = x;
        previousRight = right;
    }
    if (!(p.curve[0][0] <= p.p10 && p.p90 <= p.curve[p.curve.length - 1][0]))
        throw new Error("preview must span percentiles");
}
/** Validate the answer against its actual question space. */
function validateForecast(space, forecast) {
    if (forecast.form === "continuous") {
        if (space.kind !== "continuous" && space.kind !== "numeric")
            throw new Error("continuous forecasts require a numerical space");
        validateContinuousForecast(forecast);
        return;
    }
    const expected = familyOf(space);
    const actual = forecast.form === "bernoulli" ? "boolean" : "distribution";
    if (expected !== actual) {
        throw new Error(`forecast is ${actual} but the space calls for ${expected}`);
    }
    validatePmf(massOf(forecast), space);
}
/**
 * Throw unless a trigger's branches average back to `forecast`.
 *
 * The law of total probability, slot by slot:
 *
 *     forecast[i] === p_occurs * if_occurs[i] + (1 - p_occurs) * if_not[i]
 *
 * It is what makes a trigger's numbers *mean* something relative to the answer
 * beside them. A trigger that fails it is claiming an effect the forecast
 * contradicts, which no consumer can detect from either one alone.
 */
function validateDecomposition(forecast, score, tol = TOLERANCE) {
    const headline = massOf(forecast);
    const occurs = massOf(score.if_occurs);
    const otherwise = massOf(score.if_not);
    if (occurs.length !== headline.length || otherwise.length !== headline.length) {
        throw new Error(`branches must match the forecast's support ` +
            `(${occurs.length}, ${otherwise.length} vs ${headline.length})`);
    }
    // A two-slot PMF and a probability have the same length, not the same space.
    if (forecast.form !== score.if_occurs.form || forecast.form !== score.if_not.form) {
        throw new Error(`branches (${score.if_occurs.form}, ${score.if_not.form}) must take the forecast's shape (${forecast.form})`);
    }
    headline.forEach((expected, i) => {
        const implied = score.p_occurs * occurs[i] + (1 - score.p_occurs) * otherwise[i];
        if (Math.abs(implied - expected) > tol) {
            throw new Error(`slot ${i} implies ${implied}, but the forecast says ${expected}`);
        }
    });
}
/**
 * How far a trigger moves the answer, regardless of which way.
 *
 * The total-variation distance between its branches, so it is one number in
 * [0, 1] whatever the question's shape, and on a binary question exactly
 * `|a - b|`. Derived rather than carried, so a trigger cannot claim an effect its
 * own numbers contradict.
 */
function impact(score) {
    const occurs = massOf(score.if_occurs);
    const otherwise = massOf(score.if_not);
    return 0.5 * occurs.reduce((acc, a, i) => acc + Math.abs(a - otherwise[i]), 0);
}
/**
 * How much the trigger occurring moves the mass on one slot, signed.
 *
 * Positive when the trigger makes that outcome more likely. On a binary question
 * `shiftToward(score, 1)` is the signed move on YES — what a direction flag used
 * to say, and unlike a flag it stays meaningful when the question has three
 * answers.
 */
function shiftToward(score, slot) {
    return massOf(score.if_occurs)[slot] - massOf(score.if_not)[slot];
}
/** Whether the run has ended, however it ended. */
function isFinished(state) {
    return state.outcome !== null;
}
/** Whether `part` will still be revised. Port of `ForecastInProgress.is_final`. */
function isFinal(state, part) {
    return isFinished(state) || state.finalized.includes(part);
}
/**
 * Calls that were started and never finished, in arrival order.
 *
 * What a producer's exit path closes, and what a renderer shows as still
 * spinning. Derived from `activity` rather than tracked beside it: the log is
 * the forecast, so a second copy of this could disagree with it.
 */
function openToolCalls(state) {
    const finished = new Set(state.activity.filter((c) => c.kind === "tool_call_finished").map((c) => c.call_id));
    return state.activity.filter((c) => c.kind === "tool_call_started" && !finished.has(c.call_id));
}
/** An empty document, before any update has been folded in. */
exports.EMPTY_FORECAST = Object.freeze({
    trace_id: "",
    seq: -1,
    outcome: null,
    detail: null,
    finalized: [],
    question: "",
    space: null,
    contract: null,
    forecast: null,
    report: null,
    pastTriggers: [],
    futureTriggers: [],
    activity: [],
    unknown: [],
});
/** Structural equality over JSON values, key order aside. */
function sameJson(a, b) {
    if (a === b)
        return true;
    if (typeof a !== "object" || typeof b !== "object" || a === null || b === null)
        return false;
    if (Array.isArray(a) || Array.isArray(b)) {
        return (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => sameJson(v, b[i])));
    }
    const left = a;
    const right = b;
    const keys = Object.keys(left);
    return (keys.length === Object.keys(right).length &&
        keys.every((key) => Object.prototype.hasOwnProperty.call(right, key) && sameJson(left[key], right[key])));
}
/**
 * The value a space field takes when it is absent, as the contract declares it (Python's
 * models fill these in, so two spellings of one space compare equal there).
 */
const SPACE_DEFAULTS = Object.freeze({
    allow_other: false,
    open_low: false,
    open_high: false,
    lower_closed: true,
    upper_closed: true,
    integers: false,
    precision_days: 1 / 86400,
});
/**
 * `value` restricted to the `fields` its shape declares, each read by `nested` when that
 * names one, with nulls and declared defaults left out. A non-object is returned as is.
 */
function declared(value, fields, nested = {}) {
    if (!isRecord(value))
        return value;
    const out = {};
    for (const key of fields) {
        const field = value[key];
        if (field === null || field === undefined)
            continue;
        if (Object.prototype.hasOwnProperty.call(SPACE_DEFAULTS, key) && field === SPACE_DEFAULTS[key])
            continue;
        const read = nested[key];
        out[key] = read ? read(field) : field;
    }
    return out;
}
/** The fields each metric kind declares (`DiscreteMetric` … `MatrixMetric`). */
const METRIC_FIELDS = Object.freeze({
    discrete: ["kind"],
    abs_diff: ["kind"],
    tree: ["kind", "parents"],
    embedding: ["kind", "vectors"],
    matrix: ["kind", "matrix"],
});
/**
 * `table[key]` when `key` is one of the table's own keys, else undefined. A wire string such
 * as `"toString"` or `"__proto__"` must never reach an inherited property.
 */
function ownEntry(table, key) {
    return typeof key === "string" && Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;
}
function isNumberRows(value) {
    return Array.isArray(value) && value.every((row) => Array.isArray(row) && row.every((x) => typeof x === "number"));
}
/**
 * Whether `value` is a metric this build can read: a known `kind` (Python's `Metric` union)
 * with the field that kind requires.
 */
function isMetric(value) {
    if (!isRecord(value) || ownEntry(METRIC_FIELDS, value["kind"]) === undefined)
        return false;
    switch (value["kind"]) {
        case "tree": {
            const parents = value["parents"];
            return Array.isArray(parents) && parents.every((parent) => Number.isInteger(parent));
        }
        case "embedding":
            return isNumberRows(value["vectors"]);
        case "matrix":
            return isNumberRows(value["matrix"]);
        default:
            return true;
    }
}
const readMetric = (metric) => {
    const fields = isRecord(metric) ? ownEntry(METRIC_FIELDS, metric["kind"]) : undefined;
    return fields ? declared(metric, fields) : metric;
};
const readBinning = (binning) => declared(binning, ["edges", "edge_owners", "open_low", "open_high", "rep_low", "rep_high"]);
const readDomain = (domain) => declared(domain, ["lower", "upper", "lower_closed", "upper_closed", "integers"]);
/** An RFC 3339 / ISO 8601 timestamp with an offset, in the spellings pydantic accepts. */
const AWARE_TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})[Tt ](\d{2}):(\d{2})(?::(\d{2})(?:[.,](\d+))?)?(?:[Zz]|([+-])(\d{2}):?(\d{2})?)$/;
/**
 * An aware timestamp compares as its instant, as Python's `AwareDatetime` does: offsets are
 * normalized, and the fraction keeps six digits (pydantic drops the rest). The instant is
 * whole UTC seconds plus a microsecond remainder, kept apart: one number of microseconds
 * would pass 2^53 after about 2255 and merge neighbours. The year is set as written
 * (`Date.UTC` reads 0–99 as 1900–1999). A timestamp that is not a real calendar time
 * compares as given.
 */
const readInstant = (origin) => {
    const parts = typeof origin === "string" ? AWARE_TIMESTAMP.exec(origin) : null;
    if (!parts)
        return origin;
    const [, year, month, day, hour, minute, second, fraction, sign, offsetHours, offsetMinutes] = parts;
    const at = new Date(0);
    at.setUTCFullYear(Number(year), Number(month) - 1, Number(day));
    at.setUTCHours(Number(hour), Number(minute), Number(second ?? 0), 0);
    const real = at.getUTCFullYear() === Number(year) &&
        at.getUTCMonth() === Number(month) - 1 &&
        at.getUTCDate() === Number(day) &&
        at.getUTCHours() === Number(hour) &&
        at.getUTCMinutes() === Number(minute) &&
        at.getUTCSeconds() === Number(second ?? 0);
    if (!real)
        return origin;
    const offset = sign ? (sign === "-" ? -1 : 1) * (Number(offsetHours) * 3600 + Number(offsetMinutes ?? 0) * 60) : 0;
    const micros = Number(`${fraction ?? ""}000000`.slice(0, 6));
    return { utcSeconds: at.getTime() / 1000 - offset, micros };
};
const readTimeAxis = (axis) => declared(axis, ["origin", "coordinate", "cap", "precision_days"], { origin: readInstant });
/** Each space variant's declared fields, and how the shapes nested in it are read. */
const SPACE_SHAPES = Object.freeze({
    boolean: [["kind"], {}],
    categorical: [["kind", "labels", "metric", "allow_other"], { metric: readMetric }],
    structured: [["kind", "labels", "metric"], { metric: readMetric }],
    numeric: [["kind", "lo", "hi", "unit", "binning"], { binning: readBinning }],
    continuous: [["kind", "domain", "unit", "time_axis"], { domain: readDomain, time_axis: readTimeAxis }],
});
/**
 * The part of a space that equality compares: the fields its variant (and each shape nested
 * in it) declares, without nulls or declared defaults. Python's models ignore any other field
 * and fill in the defaults, so this is the space as Python compares it; the extras still ride
 * the wire untouched.
 */
function spaceAsCompared(space) {
    const shape = ownEntry(SPACE_SHAPES, space.kind);
    return shape ? declared(space, shape[0], shape[1]) : space;
}
/** Whether two spaces are the same space, however each spelled its defaults or extras. */
function sameSpace(a, b) {
    return sameJson(spaceAsCompared(a), spaceAsCompared(b));
}
/**
 * The fields a newly stated `space` sets. The space alone when it is the one already in
 * force; a different space drops what was read against the old one (the headline, every
 * past trigger, each future trigger's score), for the producer to restate. Port of
 * `sooth_idl.progress._space_in_force`.
 */
function spaceInForce(state, space) {
    if (state.space === null || sameSpace(state.space, space))
        return { space };
    return {
        space,
        forecast: null,
        pastTriggers: [],
        futureTriggers: state.futureTriggers.map(({ score: _score, ...outlined }) => outlined),
    };
}
function upsert(existing, incoming) {
    const index = existing.findIndex((current) => current.id === incoming.id);
    if (index === -1)
        return [...existing, incoming];
    return existing.map((current, i) => (i === index ? incoming : current));
}
/**
 * Fold one update into `state`, returning the new state.
 *
 * Last write wins, per entity: this is the only place the revision semantics
 * live, so no consumer re-implements them. An unrecognized update is collected
 * rather than dropped.
 *
 * Out-of-order and duplicate delivery are NOT handled here — `seq` is on every
 * update precisely so a caller that needs those guarantees can order or
 * deduplicate before folding, and one that does not need them pays nothing.
 */
function applyUpdate(state, update) {
    if (isUnknownUpdate(update)) {
        // The cursor advances over an unreadable update too: a client that could not
        // render it has still SEEN it, and must not be sent it again.
        return {
            ...state,
            seq: update.seq ?? state.seq,
            unknown: [...state.unknown, update],
        };
    }
    // Read as the parser reads it, against the space in force when there is one: an update
    // built in code rather than parsed gets the same checks, and one that fails them is kept
    // as unreadable.
    const checked = parseUpdate(update, state.space ?? undefined);
    if (isUnknownUpdate(checked))
        return applyUpdate(state, checked);
    const base = { ...state, trace_id: update.trace_id, seq: update.seq };
    switch (update.kind) {
        case "stream_opened":
            return { ...base, question: update.question, ...spaceInForce(state, update.space) };
        case "contract_updated":
            return { ...base, contract: update.contract, ...spaceInForce(state, update.contract.outcome_space) };
        case "forecast_updated":
        case "continuous_forecast_updated":
            return { ...base, forecast: update.forecast };
        case "report_updated":
            return { ...base, report: update.report };
        case "past_trigger_upserted":
            return { ...base, pastTriggers: upsert(state.pastTriggers, update.trigger) };
        case "future_trigger_upserted":
            return { ...base, futureTriggers: upsert(state.futureTriggers, update.trigger) };
        case "tool_call_started":
        case "tool_call_updated":
        case "tool_call_finished":
            // An occurrence: appended, never folded. Replacing the previous tool call
            // would lose the run's history, which is the half of this stream that
            // makes it auditable.
            return { ...base, activity: [...state.activity, update] };
        case "trigger_removed":
            return {
                ...base,
                pastTriggers: state.pastTriggers.filter((t) => t.id !== update.trigger_id),
                futureTriggers: state.futureTriggers.filter((t) => t.id !== update.trigger_id),
            };
        case "run_finished":
            return { ...base, outcome: update.outcome, detail: update.detail ?? null };
        case "part_finalized":
            return { ...base, finalized: [...new Set([...state.finalized, update.part])] };
    }
}
/**
 * Everything in `updates` after `afterSeq`, in order — the resume slice.
 *
 * A client that persisted through `seq = n` reconnects asking for `> n`, so a
 * dropped connection costs the tail rather than the whole run. Pass `-1` for a
 * client that has seen nothing, which is what `EMPTY_FORECAST` starts at, so
 * `updatesSince(log, state.seq)` is the resume request either way.
 *
 * This works only because `seq` is assigned when a producer PERSISTS an update
 * rather than when it sends one: the same update must carry the same `seq` on a
 * replay as it did live, or a resuming client silently skips or repeats work.
 */
function updatesSince(updates, afterSeq) {
    // Only an `UnknownUpdate` can lack a `seq`, and only because its producer
    // sent nothing usable. Such an update has no position, so a real resume omits
    // it — the producer cannot know whether the client already has it, and
    // `unknown` accumulates rather than folding, so re-sending would double it. A
    // client that has seen NOTHING is the one unambiguous case: everything after
    // nothing is the whole log. That is what makes
    // `replay(updatesSince(log, EMPTY_FORECAST.seq))` equal `replay(log)` exactly,
    // rather than only for logs that happen to contain no such update.
    const fromScratch = afterSeq < 0;
    return updates.filter((update) => (update.seq === null ? fromScratch : update.seq > afterSeq));
}
/**
 * Fold a whole log — or a resume slice onto existing state — into one document.
 *
 * `replay(updatesSince(log, state.seq), state)` is exactly a reconnect, and it
 * produces the same document as folding the log from the beginning, which is the
 * property that makes resuming safe to do at all.
 */
function replay(updates, state = exports.EMPTY_FORECAST) {
    return updates.reduce(applyUpdate, state);
}
